// frontend/src/components/onboarding/firstStepsSkips.js
//
// The first-steps card's browser-local "not now" answers (owner decision D4),
// as a tiny external store so a surface OUTSIDE the dashboard can follow them.
//
// Two outside readers:
//   - the iOS install snackbar (`PwaAwarenessSnackbar`, mounted app-globally
//     in App.js) waits until the card's PUSH step is done or skipped
//     (`installAwaitsFirstStepsPush`, utils/onboardingSelectors.js) - on an
//     iPhone outside the installed app that step IS the install ask, so the
//     two never ask at once. A skip releases it on the NEXT page load, never
//     in the view where the user just said "not now"
//     (`useSkippedThisPageView` holds it down meanwhile).
//   - the push nudge (`PushEnableNudge`) counts a push skip pressed in this
//     page view as a dismissal (`wasSkippedThisPageView`).
//
// STORAGE: a key's value is "1" once skipped. When storage refuses the write
// (Safari private mode, a full quota) the skip is kept in memory for the page
// view instead - the card's fallback since WP2, now shared.

import { useSyncExternalStore } from "react";

/** The browser-local "not now" answers. A key's value is "1" once skipped. */
export const SKIP_STORAGE_KEYS = Object.freeze({
  push: "leadify_first_steps_push_skipped",
  contacted: "leadify_first_steps_contacted_skipped",
});

/** Skips storage refused to keep: they hold for this page view only. */
const pageViewSkips = new Set();
/**
 * EVERY skip pressed in this page view, stored or not. Read by the surfaces
 * that must not react to a skip until the NEXT page load: the push nudge
 * (never asks again the moment the card retires) and the iOS install snackbar
 * (released by a push skip on the next load, never seconds after it).
 */
const skippedThisView = new Set();
const listeners = new Set();

const notify = () => {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      /* one bad subscriber must not starve the others */
    }
  });
};

/** Was this step skipped in this browser (or, storage refused, this view)? */
export const readStoredSkip = (id) => {
  try {
    if (window.localStorage.getItem(SKIP_STORAGE_KEYS[id]) === "1") return true;
  } catch {
    /* unreadable storage: the page-view answer below is all there is */
  }
  return pageViewSkips.has(id);
};

/**
 * Remember a skip in this browser and wake every reader. Best effort: blocked
 * or full storage keeps it for the page view instead.
 *
 * @returns {boolean} whether it was stored
 */
export const storeSkip = (id) => {
  skippedThisView.add(id);
  let stored = false;
  try {
    window.localStorage.setItem(SKIP_STORAGE_KEYS[id], "1");
    stored = true;
  } catch {
    pageViewSkips.add(id);
  }
  notify();
  return stored;
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * React binding: `true` once `id` was skipped in this browser.
 *
 * @param {"push"|"contacted"} id
 * @returns {boolean}
 */
export const useStoredSkip = (id) =>
  useSyncExternalStore(
    subscribe,
    () => readStoredSkip(id),
    () => false
  );

/** Was `id` skipped in THIS page view (whether or not storage kept it)? */
export const wasSkippedThisPageView = (id) => skippedThisView.has(id);

/**
 * React binding of `wasSkippedThisPageView`, live: flips to `true` the moment
 * the skip is pressed.
 *
 * @param {"push"|"contacted"} id
 * @returns {boolean}
 */
export const useSkippedThisPageView = (id) =>
  useSyncExternalStore(
    subscribe,
    () => skippedThisView.has(id),
    () => false
  );

/**
 * Test-only: forget this page view's skips (module state outlives a test) -
 * also how a test stands in for the NEXT page load.
 */
export function __resetFirstStepsSkipsForTests() {
  pageViewSkips.clear();
  skippedThisView.clear();
  notify();
}
